import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import '../styles/AdminModals.css';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'https://mammotz-backend.onrender.com';

const MessagesModal = ({ onClose }) => {
  const { translations } = useLanguage();
  const { token } = useAuth();
  const [activeUser, setActiveUser] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [polling, setPolling] = useState(false);

  const fetchConversations = useCallback(async () => {
    if (polling) return;
    setPolling(true);
    try {
      const response = await fetch(`${API_URL}/api/messages/conversations`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch conversations');
      }

      const data = await response.json();
      setConversations(data);
    } catch (err) {
      setError('Failed to load conversations');
      console.error('Error fetching conversations:', err);
    } finally {
      setPolling(false);
    }
  }, [token, polling]);

  const fetchUserMessages = useCallback(async (userId) => {
    try {
      const response = await fetch(`${API_URL}/api/messages/conversation/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch messages');
      }

      const userMessages = await response.json();
      setMessages(userMessages);
    } catch (err) {
      setError('Failed to load messages');
      console.error('Error fetching messages:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchConversations();
    // Start polling for new conversations
    const interval = setInterval(fetchConversations, 10000);
    return () => clearInterval(interval);
  }, [fetchConversations]);

  // Sort conversations by last message timestamp (assuming backend provides last_message_timestamp)
  const sortedConversations = [...conversations].sort((a, b) => {
    const aTime = new Date(a.last_message_timestamp || 0);
    const bTime = new Date(b.last_message_timestamp || 0);
    return bTime - aTime; // Most recent first
  });

  useEffect(() => {
    if (activeUser) {
      fetchUserMessages(activeUser.id);
    }
  }, [activeUser, fetchUserMessages]);

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !activeUser) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/messages/reply/${activeUser.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: replyText
        })
      });

      if (!response.ok) {
        throw new Error('Failed to send reply');
      }

      // Refresh messages and conversations
      await fetchUserMessages(activeUser.id);
      await fetchConversations();
      setReplyText('');
    } catch (err) {
      setError('Failed to send reply');
      console.error('Error sending reply:', err);
    } finally {
      setLoading(false);
    }
  };

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.3 } }
  };

  const modalVariants = {
    hidden: { scale: 0.9, opacity: 0, y: 20 },
    visible: {
      scale: 1,
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
        duration: 0.4
      }
    },
    exit: {
      scale: 0.9,
      opacity: 0,
      y: 20,
      transition: { duration: 0.3 }
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="admin-modal-overlay"
        onClick={onClose}
        variants={overlayVariants}
        initial="hidden"
        animate="visible"
        exit="hidden"
      >
        <motion.div
          className="admin-modal messages-modal"
          onClick={(e) => e.stopPropagation()}
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          <motion.button
            className="admin-modal-close"
            onClick={onClose}
            whileHover={{ scale: 1.1, rotate: 90 }}
            whileTap={{ scale: 0.9 }}
            transition={{ type: "spring", stiffness: 400 }}
          >
            ×
          </motion.button>
          <motion.h2
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.3 }}
          >
            {translations.admin.messagesModal.title}
          </motion.h2>

          {error && (
            <motion.div
              className="error-message"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              {error}
            </motion.div>
          )}

          <motion.div
            className="messages-container"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.4 }}
          >
            <motion.div
              className="users-list"
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.3 }}
            >
              <h3>{translations.admin.messagesModal.users}</h3>
              {sortedConversations.map((user, index) => (
                <motion.div
                  key={user.id}
                  className={`user-item ${activeUser?.id === user.id ? 'active' : ''}`}
                  onClick={() => setActiveUser(user)}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 + index * 0.05, duration: 0.2 }}
                  whileHover={{ scale: 1.02, backgroundColor: "rgba(147, 155, 153, 0.1)" }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="user-name">{user.name}</div>
                  {user.unread_count > 0 && (
                    <motion.div
                      className="unread-badge"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 500 }}
                    >
                      {user.unread_count}
                    </motion.div>
                  )}
                </motion.div>
              ))}
              {conversations.length === 0 && (
                <motion.div
                  className="no-users"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5, duration: 0.3 }}
                >
                  No users with messages
                </motion.div>
              )}
            </motion.div>

            <motion.div
              className="conversation-view"
              initial={{ x: 20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.3 }}
            >
              {activeUser ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <h3>{translations.admin.messagesModal.conversationWith} {activeUser.name}</h3>
                  <motion.div
                    className="messages-list"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.1, duration: 0.3 }}
                  >
                    {messages.map((msg, index) => (
                      <motion.div
                        key={msg.id}
                        className={`message ${msg.sender_role === 'admin' ? 'admin' : 'user'}`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05, duration: 0.2 }}
                      >
                        {msg.message}
                        <div className="message-time">
                          {new Date(msg.timestamp).toLocaleString()}
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                  <motion.form
                    onSubmit={handleSendReply}
                    className="reply-box"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.3 }}
                  >
                    <input
                      type="text"
                      placeholder={translations.admin.messagesModal.typeReply}
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      disabled={loading}
                    />
                    <motion.button
                      type="submit"
                      disabled={loading || !replyText.trim()}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      transition={{ type: "spring", stiffness: 400 }}
                    >
                      {loading ? '...' : translations.admin.messagesModal.send}
                    </motion.button>
                  </motion.form>
                </motion.div>
              ) : (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5, duration: 0.3 }}
                >
                  {translations.admin.messagesModal.selectUser}
                </motion.p>
              )}
            </motion.div>
          </motion.div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default MessagesModal;
